import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { toast } from 'sonner';

import { useUpdateCall } from './useCalls';

/** Return type for useUpdateChartNotes hook */
export interface UseUpdateChartNotesReturn {
  /** Function to update chart notes on a call */
  updateChartNotes: (callId: string, notes: string) => Promise<void>;
}

/**
 * Custom hook for updating chart notes on a call.
 * Provides error handling with toast notifications.
 *
 * @param caseRecordId - The case record ID
 * @returns Object containing the update function
 */
export const useUpdateChartNotes = (caseRecordId: string | null): UseUpdateChartNotesReturn => {
  const { mutateAsync } = useUpdateCall();
  const queryClient = useQueryClient();

  const updateChartNotes = useCallback(
    async (callId: string, notes: string): Promise<void> => {
      if (!caseRecordId) {
        toast.error('Cannot update notes: case record not found');
        return;
      }

      try {
        await mutateAsync(
          {
            caseRecordId,
            callId,
            data: {
              chart_info: {
                notes,
              },
            },
          },
          {
            onSettled: () => {
              queryClient.invalidateQueries({
                queryKey: ['caseRecordCalls', caseRecordId],
              });
            },
          },
        );
        toast.success('Notes saved successfully');
      } catch (error: any) {
        const message = error.response?.data?.message || 'Failed to save notes';
        toast.error(message);
      }
    },
    [caseRecordId, mutateAsync, queryClient],
  );

  return {
    updateChartNotes,
  };
};
